import * as React from 'react';
import {
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  isSameWeek,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Fab, Grid2, Typography, useMediaQuery } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { View } from 'react-big-calendar';
import { Appointment, Filter, FilterOption, LoadedUser } from '../interfaces';
import DateNav from '../components/navigation/DateNav';
import MultiSelectFilter from '../components/filtering/MultiSelectFilter';
import FullPageModal from '../components/modal/FullPageModal';
import {
  getAppointmentById,
  invalidateAppointmentPayments,
  invalidateDepositTransferAppointments,
} from '../services/appointments';
import Error from '../components/global/Error';
import { important, isUserAdminOrManager } from '../utils/global/helper';
import WeekView from '../components/calendar/week/weekView';
import SingleSelectFilter from '../components/filtering/SingleSelectFilter';
import '../components/calendar/calendar.css';
import client from '../utils/react-query/client';
import useUser from '../hooks/global/useUser';
import useGetAppointments from '../hooks/appointments/useGetAppointments';
import useCheckoutForm from '../hooks/global/useCheckoutForm';
import useCalendarSelectedAppointment from '../hooks/appointments/useCalendarSelectedAppointment';
import useCheckoutSocket from '../hooks/payments/useCheckoutSocket';
import useFeatureGuard from '../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../types/subscriptions';
import useSubscriptionModal from '../hooks/subscriptions/useSubscriptionModal';
import PageLayout from '../components/global/PageLayout';
import { getBusinessTripName } from '../helpers/businesses';
import OnboardingProgress from '../components/onboarding/OnboardingProgress';
import { PAGE_PADDING } from '../constants/ui';
import { FILTER_PERSISTENCE_KEYS, ROUTES } from '../constants/global';
import CalendarNewForm from '../components/form/CalendarNewForm';
import AppointmentSideBarModal from '../components/appointments/AppointmentSideBarModal';
import {
  getInitialArtistOptions,
  getInitialSelectedArtists,
} from '../helpers/employees';
import { getCheckoutErrorMessage } from '../helpers/payments';
import useInSplitCheckout from '../hooks/payments/useInSplitCheckout';
import AlertTooltip from '../components/global/AlertTooltip';

const DATE_STORAGE_KEY = 'CalendarSelectedDate';
const VIEW_STORAGE_KEY = 'CalendarSelectedView';
const ARTIST_FILTER_NAME = FILTER_PERSISTENCE_KEYS.calendarArtists;
const ARTIST_COLOR_TOOLTIP_STORAGE_KEY = 'CalendarArtistColorTooltip';
const ARTIST_COLOR_TOOLTIP_AUTO_OPEN_UNTIL_DATE = new Date('2025-06-15');

const getInitialView = (options: FilterOption[], defaultView: string) => {
  const cachedValue = sessionStorage.getItem(VIEW_STORAGE_KEY);

  const parsedCachedValue = cachedValue ? JSON.parse(cachedValue) : undefined;

  if (options.some(option => option.value === parsedCachedValue)) {
    return parsedCachedValue;
  }

  return defaultView;
};

const getInitialSelectedDate = () => {
  const cachedValue = sessionStorage.getItem(DATE_STORAGE_KEY);
  const parsedCachedValue = cachedValue ? new Date(cachedValue) : undefined;
  return parsedCachedValue ?? new Date();
};

export default function CalendarPage() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  const { user: loadedUser } = useUser<LoadedUser>();
  const {
    selectedAppointment,
    selectedAppointmentUpdated,
    setSelectedAppointment,
    setSelectedAppointmentUpdated,
    openedAppointment,
    setOpenedAppointment,
  } = useCalendarSelectedAppointment();
  const {
    resetCheckoutFailureData,
    setCheckoutFailureData,
    setTerminalCheckoutModalToFailed,
  } = useCheckoutForm();

  const isDesktop = useMediaQuery('(min-width:900px)');

  const [filteredAppointments, setFilteredAppointments] =
    React.useState<Appointment[]>();

  const artistOptions = React.useMemo(() => {
    if (!loadedUser) return [];
    return getInitialArtistOptions(loadedUser);
  }, [loadedUser]);

  const [selectedArtists, setSelectedArtists] = React.useState(() =>
    getInitialSelectedArtists(loadedUser, artistOptions, ARTIST_FILTER_NAME),
  );

  const artistFilter = React.useMemo<Filter>(
    () => ({
      name: ARTIST_FILTER_NAME,
      options: artistOptions,
      state: selectedArtists,
      setState: setSelectedArtists,
      emptyLabel: 'Select an artist',
      allLabel: 'All artists',
    }),
    [artistOptions, selectedArtists],
  );

  const tripsOptions = React.useMemo(() => {
    if (
      !loadedUser.business.businessTrips ||
      loadedUser.business.businessTrips.length === 0
    )
      return [];

    const tempTripOptions = [{ label: 'Home Studio', value: '' }];
    loadedUser.business.businessTrips.forEach(trip => {
      tempTripOptions.push({
        label: getBusinessTripName(trip),
        value: trip.tripId,
      });
    });
    return tempTripOptions;
  }, [loadedUser]);

  const [selectedTrip, setSelectedTrip] = React.useState<string[]>(
    tripsOptions.map(option => option.value),
  );

  const tripFilter = React.useMemo(() => {
    if (!tripsOptions || tripsOptions.length === 0) return undefined;
    return {
      name: 'Trips',
      options: tripsOptions,
      state: selectedTrip,
      setState: setSelectedTrip,
    };
  }, [selectedTrip, setSelectedTrip, tripsOptions]);

  const viewOptions = [
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Day', value: 'day' },
  ];

  const [selectedView, setSelectedView] = React.useState<View>(() =>
    getInitialView(viewOptions, isDesktop ? 'month' : 'day'),
  );

  const [viewFilter, setViewFilter] = React.useState({
    name: VIEW_STORAGE_KEY,
    options: [
      { label: 'Week', value: 'week' },
      { label: 'Month', value: 'month' },
      { label: 'Day', value: 'day' },
    ],
    state: selectedView,
    setState: setSelectedView,
  });

  const [selectedDate, setSelectedDate] = React.useState(() =>
    getInitialSelectedDate(),
  );
  const [dataStartDate, setDataStartDate] = React.useState(
    startOfWeek(startOfMonth(new Date())),
  );
  const [dataEndDate, setDataEndDate] = React.useState(
    endOfWeek(endOfMonth(new Date())),
  );

  const [newAptModalOpen, setNewAptModalOpen] = React.useState(false);
  const [shouldScrollToTop, setShouldScrollToTop] = React.useState(false);

  const {
    appointments,
    isError: isErrorAppointments,
    queryKey,
    refetch: refetchAppts,
  } = useGetAppointments({
    dataEndDate,
    dataStartDate,
    refetchInterval: 300000,
  });

  const {
    resetTerminalCheckoutModalState,
    setTerminalCheckoutModalToSuccess,
    mostRecentPaymentId,
    terminalCheckoutModal,
  } = useCheckoutForm();

  const { openSubscriptionModal } = useSubscriptionModal();
  const canAddAppointment = useFeatureGuard(Feature.ADD_APPOINTMENT);
  const canManageAppointment = useFeatureGuard(Feature.MANAGE_APPOINTMENT);
  const canManageTeam = useFeatureGuard(Feature.MANAGE_TEAM);

  // Set up click action for each row of the calendar table
  const editAppointment = React.useCallback(() => {
    if (!openedAppointment) return;
    if (!canManageAppointment) {
      openSubscriptionModal();
      return;
    }
    setSelectedAppointment(openedAppointment);
    setNewAptModalOpen(true);
  }, [
    canManageAppointment,
    openSubscriptionModal,
    openedAppointment,
    setSelectedAppointment,
  ]);

  // Close edit appointment
  const closeEditAppointment = React.useCallback(() => {
    setSelectedAppointment(null);
    setNewAptModalOpen(false);
  }, [setSelectedAppointment]);

  // Set up click action for each row of the calendar table
  const openAppointment = React.useCallback(
    (appointment: Appointment) => {
      setOpenedAppointment(appointment);
      resetCheckoutFailureData();
    },
    [resetCheckoutFailureData, setOpenedAppointment],
  );

  const onCloseAppointment = React.useCallback(() => {
    setOpenedAppointment(null);
    if (appointmentId) {
      navigate(ROUTES.calendar, {
        replace: true,
        preventScrollReset: true,
      });
    }
  }, [appointmentId, navigate, setOpenedAppointment]);

  const handleOpen = () => {
    if (!canAddAppointment) {
      openSubscriptionModal();
      return;
    }
    setNewAptModalOpen(true);
  };

  const changeStartDate = (newDate: Date) => {
    setSelectedDate(newDate);
  };

  const updateAppointmentInArray = React.useCallback(
    (newAppointment: Appointment) =>
      client.setQueryData(
        queryKey,
        (oldAppointments: Appointment[] | undefined) => {
          const currentAppointments = oldAppointments ?? [];
          const index = currentAppointments.findIndex(
            a => a.appointmentId === newAppointment.appointmentId,
          );
          if (index > -1) {
            return currentAppointments.map((appointment, idx) =>
              idx === index ? newAppointment : appointment,
            );
          }
          return [...currentAppointments, newAppointment];
        },
      ),
    [queryKey],
  );

  const terminalCheckoutModalRef = React.useRef(terminalCheckoutModal);
  React.useEffect(() => {
    terminalCheckoutModalRef.current = terminalCheckoutModal;
  }, [terminalCheckoutModal]);

  const fetchAppointmentAndUpdateModal = React.useCallback(
    async (tempAppointmentId: string, isError?: boolean) => {
      if (!loadedUser) return;
      const response = await getAppointmentById(tempAppointmentId);

      if (
        response &&
        !response.checkoutSessionActive &&
        terminalCheckoutModalRef.current.status === 'loading' &&
        !isError
      ) {
        resetTerminalCheckoutModalState();
      }

      // Update the appointment in the appointments array
      updateAppointmentInArray(response);
      openAppointment(response);
    },
    [
      loadedUser,
      updateAppointmentInArray,
      openAppointment,
      resetTerminalCheckoutModalState,
    ],
  );

  React.useEffect(() => {
    if (selectedAppointmentUpdated && openedAppointment) {
      fetchAppointmentAndUpdateModal(openedAppointment.appointmentId);
      setSelectedAppointment(null);
      setSelectedAppointmentUpdated(false);
    }
  }, [
    setSelectedAppointment,
    setSelectedAppointmentUpdated,
    fetchAppointmentAndUpdateModal,
    openedAppointment,
    selectedAppointmentUpdated,
  ]);

  React.useEffect(() => {
    if (appointmentId) {
      fetchAppointmentAndUpdateModal(appointmentId);
    }
  }, [appointmentId, fetchAppointmentAndUpdateModal]);

  const isInSplitCheckout = useInSplitCheckout();

  // Backup interval fetching in PaymentSection
  useCheckoutSocket({
    enabled: !isInSplitCheckout,
    onCheckoutCompleted: async data => {
      if (
        (!data.appointmentId ||
          data.appointmentId !== openedAppointment?.appointmentId) &&
        (!data.paymentId || data.paymentId !== mostRecentPaymentId)
      ) {
        return;
      }

      if (data.appointmentId) {
        invalidateDepositTransferAppointments();
        await fetchAppointmentAndUpdateModal(data.appointmentId);
      }

      if (terminalCheckoutModal.status === 'loading') {
        setTerminalCheckoutModalToSuccess();
      } else {
        resetTerminalCheckoutModalState();
      }
    },
    onCheckoutFailed: async data => {
      if (
        (!data.appointmentId ||
          data.appointmentId !== openedAppointment?.appointmentId) &&
        (!data.paymentId || data.paymentId !== mostRecentPaymentId)
      ) {
        return;
      }

      setCheckoutFailureData(
        data as Parameters<typeof setCheckoutFailureData>[0],
      );
      const tempFailureMessage = getCheckoutErrorMessage(data);

      setTerminalCheckoutModalToFailed(tempFailureMessage);
      if (data.appointmentId)
        await fetchAppointmentAndUpdateModal(data.appointmentId, true);
    },
    onCheckoutUpdated: async data => {
      if (
        !data.appointmentId ||
        data.appointmentId !== openedAppointment?.appointmentId
      ) {
        return;
      }

      await fetchAppointmentAndUpdateModal(data.appointmentId);
      invalidateAppointmentPayments(data.appointmentId);
    },
  });

  React.useEffect(() => {
    if (!appointments) return;
    let tempFilteredAppointments = appointments;

    if (selectedArtists) {
      if (selectedArtists.length !== 1 || selectedArtists[0] !== 'Initialize') {
        tempFilteredAppointments = tempFilteredAppointments.filter(
          (appointment: Appointment) =>
            selectedArtists.includes(appointment.employeeId),
        );
      }
    }

    if (selectedTrip && selectedTrip.length > 0) {
      tempFilteredAppointments = tempFilteredAppointments.filter(
        appointment => {
          const { businessTripId } = appointment;

          // Check if any ID in selectedTrip matches the businessTripId
          const matchesId =
            businessTripId && selectedTrip.includes(businessTripId);

          // Check if selectedTrip includes an empty string and businessTripId is null or undefined
          const matchesEmptyString =
            selectedTrip.includes('') &&
            (businessTripId === null || businessTripId === undefined);

          // Include the appointment if either condition is true
          return matchesId || matchesEmptyString;
        },
      );
    }

    setFilteredAppointments(tempFilteredAppointments);
  }, [selectedArtists, appointments, selectedTrip]);

  React.useEffect(() => {
    if (selectedView) {
      setViewFilter({
        ...viewFilter,
        state: selectedView,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView]);

  React.useEffect(() => {
    const startOfMonthData = startOfWeek(startOfMonth(selectedDate));
    const endOfMonthData = endOfWeek(endOfMonth(selectedDate));

    if (startOfMonthData < dataStartDate) {
      setDataStartDate(startOfMonthData);
      setDataEndDate(endOfMonthData);
    }

    if (endOfMonthData > dataEndDate) {
      setDataStartDate(startOfMonthData);
      setDataEndDate(endOfMonthData);
    }
  }, [dataEndDate, dataStartDate, selectedDate]);

  const error = isErrorAppointments ? 'Error loading appointments' : '';

  const returnToToday = () => {
    setSelectedDate(new Date());
    sessionStorage.setItem(DATE_STORAGE_KEY, new Date().toISOString());
  };

  const showTodayButton =
    (selectedView === 'day' && !isSameDay(new Date(), selectedDate)) ||
    (selectedView === 'week' && !isSameWeek(new Date(), selectedDate)) ||
    (selectedView === 'month' && !isSameMonth(new Date(), selectedDate));

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 200px)',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Error errorMessage={error} />
      </Box>
    );
  }

  const showArtistFilterTooltip =
    canManageTeam &&
    loadedUser?.canManageTeam &&
    isUserAdminOrManager(loadedUser) &&
    !!loadedUser?.business?.employees?.length &&
    !loadedUser.business.employees.some(e => e.color);

  return (
    <PageLayout
      title="Calendar"
      topSection={
        <OnboardingProgress
          sx={theme => ({
            mt: 4,
            borderTop: `1px solid ${theme.palette.lightGrey.main}`,
            borderBottom: `1px solid ${theme.palette.lightGrey.main}`,
            mx: PAGE_PADDING,
          })}
        />
      }>
      <Box
        className="header-right"
        sx={{
          display: {
            xs: 'inline',
            md: 'none',
          },
        }}>
        <Button
          variant="contained"
          className="primary hidden-desktop"
          onClick={handleOpen}>
          Add New
        </Button>
      </Box>
      <div className="calendar-header">
        <Grid2
          container
          spacing={2}
          style={{
            justifyContent: 'flex-start',
            alignItems: 'center',
            flex: '1 0',
          }}>
          <Grid2
            size={{
              xs: 6,
              sm: 6,
              md: 'auto',
            }}
            order={{ sm: 1, md: 2 }}>
            <SingleSelectFilter
              name={viewFilter.name}
              options={viewFilter.options}
              state={viewFilter.state}
              setState={
                viewFilter.setState as React.Dispatch<
                  React.SetStateAction<string>
                >
              }
            />
          </Grid2>
          {artistFilter && (
            <AlertTooltip
              autoOpen={showArtistFilterTooltip}
              autoOpenUntilDate={ARTIST_COLOR_TOOLTIP_AUTO_OPEN_UNTIL_DATE}
              storageKey={ARTIST_COLOR_TOOLTIP_STORAGE_KEY}
              tooltipContent={
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '12px',
                    color: '#ffffff',
                  }}>
                  You can now manage individual employees&apos; colors from the{' '}
                  <Link
                    to={ROUTES.team}
                    style={{
                      color: '#ffffff',
                    }}>
                    Manage Team
                  </Link>{' '}
                  page!
                </Typography>
              }>
              <Grid2
                size={{
                  xs: 6,
                  sm: 6,
                  md: 'auto',
                }}
                order={{ sm: 2, md: 3 }}>
                <MultiSelectFilter
                  name={artistFilter.name}
                  options={artistFilter.options}
                  state={artistFilter.state}
                  setState={artistFilter.setState}
                  emptyLabel={artistFilter.emptyLabel}
                  allLabel={artistFilter.allLabel}
                />
              </Grid2>
            </AlertTooltip>
          )}
          {tripFilter && (
            <Grid2
              size={{
                xs: 6,
                sm: 6,
                md: 'auto',
              }}
              order={{ sm: 2, md: 3 }}>
              <MultiSelectFilter
                name={tripFilter.name}
                options={tripFilter.options}
                state={tripFilter.state}
                setState={tripFilter.setState}
              />
            </Grid2>
          )}
          <Grid2
            size={{
              xs: 12,
              sm: 12,
              md: 'auto',
            }}
            order={{ sm: 3, md: 1 }}>
            <DateNav
              storageKey={DATE_STORAGE_KEY}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              currentView={selectedView}
            />
          </Grid2>
        </Grid2>
        <Box
          className="header-right"
          sx={{
            display: {
              xs: important('none'),
              md: important('inline'),
            },
          }}>
          <Button variant="contained" className="primary" onClick={handleOpen}>
            Add New
          </Button>
        </Box>
      </div>
      <WeekView
        artistOptions={artistOptions.map(option => option.value)}
        appointments={filteredAppointments}
        selectedArtists={selectedArtists || []}
        setSelectedArtists={setSelectedArtists}
        view={selectedView}
        date={selectedDate}
        setDate={changeStartDate}
        setView={setSelectedView}
        openAppointment={openAppointment}
        refreshModal={fetchAppointmentAndUpdateModal}
      />
      {openedAppointment ? (
        <AppointmentSideBarModal
          appointment={openedAppointment}
          isOpen
          onClose={onCloseAppointment}
          onRefresh={async () => {
            await fetchAppointmentAndUpdateModal(
              openedAppointment.appointmentId,
            );
          }}
          onEdit={editAppointment}
          onRefetchAppointments={async () => {
            refetchAppts();
          }}
        />
      ) : null}

      <FullPageModal
        modalOpen={newAptModalOpen}
        setModalOpen={setNewAptModalOpen}
        handleClose={closeEditAppointment}
        shouldScrollTop={shouldScrollToTop}
        setShouldScrollToTop={setShouldScrollToTop}
        name={selectedAppointment ? 'Edit Appointment' : 'Add New'}>
        <CalendarNewForm
          onClose={() => setNewAptModalOpen(false)}
          onCreateUpdateSuccess={async params => {
            const { type } = params;
            // get new appointment
            if (type === 'create') await refetchAppts();
            // update appointment
            else {
              const { appointment: appt } = params;
              if (!('appointmentId' in appt)) return;
              await fetchAppointmentAndUpdateModal(appt.appointmentId);
            }
          }}
          setShouldScrollToTop={setShouldScrollToTop}
        />
      </FullPageModal>

      {showTodayButton && (
        <Fab
          variant="extended"
          sx={{ position: 'fixed', bottom: 64, right: { xs: 32, md: 64 } }}
          color="primary"
          onClick={returnToToday}>
          <RefreshIcon sx={{ mr: 1 }} />
          <Typography variant="h5" color="white">
            Today
          </Typography>
        </Fab>
      )}
    </PageLayout>
  );
}
